import { ArrowForward, ExpandCircleDown } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography
} from '@mui/material'
import { BlockButton } from '../../../customComponents/buttons/BlockButton'
import { BudgetActivityDetails } from './BudgetActivityDetails'
import { type AnnualPlanRole } from '../../../../models/AnnualPlanRole'
import { SurpassesThreshold } from './SurpassesThreshold'
import { useState } from 'react'

interface Props {
  name: string
  amount: number
  updatedAmount: number
  needsReview?: boolean
  color: string
  viewDetails: (() => void) | null
  planOrganizationId: number
  priorityId: number
  annualPlanId: number
  rcnId: number
  userRole: AnnualPlanRole | null
  hideButtons?: boolean
  implementationPhase?: boolean
}

export const BudgetActivityDisplay = (props: Props): JSX.Element => {
  const isImplementationPhase = props.implementationPhase === true
  const [expanded, setExpanded] = useState(false)

  return <Accordion
    disableGutters={true}
    data-testid={`accordion-${props.name}`}
    sx={{ bgcolor: props.color, borderRadius: '12px !important', p: '20px' }}
    onChange={() => { setExpanded(!expanded) }}
  >
    <AccordionSummary
      expandIcon={<ExpandCircleDown sx={{ color: 'var(--white)', visibility: props.hideButtons === true ? 'hidden' : 'visible' }} data-testid={`expand-${props.name}`} />}
      role='none presentation'
      aria-expanded={undefined}
    >
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        mr: '20px'
      }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.5em' }}>
          {props.implementationPhase === true && props.needsReview === true && !expanded && (
            <SurpassesThreshold
              invert={true}
            />
          )}
          <Typography variant='h3' sx={{ color: 'white', fontWeight: 500 }}>
            {props.name}
          </Typography>
        </Box>

        {isImplementationPhase
          ? (
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant='h4' data-testid={`contractAmount-${props.name}`} sx={{ color: 'white', fontWeight: 500 }}>
                Contract: {props.amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
              </Typography>
              <Typography variant='h4' data-testid={`updatedAmount-${props.name}`} sx={{ color: 'white', fontWeight: 500 }}>
                Updates: {(props.updatedAmount ?? props.amount).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
              </Typography>
            </Box>
            )
          : (
            <Typography variant='h3' data-testid={`amount-${props.name}`} sx={{ color: 'white', fontWeight: 500 }}>
              {props.amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
            </Typography>
            )
        }
      </Box>
    </AccordionSummary>

    <AccordionDetails>
      <Stack gap='1rem'>
        <BudgetActivityDetails
          name={props.name}
          planOrganizationId={props.planOrganizationId}
          priorityId={props.priorityId}
          annualPlanId={props.annualPlanId}
          rcnId={props.rcnId}
          userRole={props.userRole}
          hideButtons={props.hideButtons}
          implementationPhase={props.implementationPhase}
        />

        {props.viewDetails != null && props.hideButtons !== true &&
          <BlockButton onClick={props.viewDetails} sx={{ width: 'fit-content' }} dataTestId={`viewDetails-${props.name}`}>
            View full details

            <ArrowForward sx={{ ml: '.25em' }} />
          </BlockButton>
        }
      </Stack>
    </AccordionDetails>
  </Accordion>
}
